<template>
  <div class="bar-chart-wrap" v-if="headers.length && groups.length">
    <div class="svg-scroller">
      <svg ref="svgEl" :width="chartWidth" :height="svgHeight">
        <rect :width="chartWidth" :height="svgHeight" fill="#fff" />
        <g :transform="`translate(${margin.left},${legendTop})`">
          <g
            v-for="(it, i) in legendItems"
            :key="`lg-${i}`"
            :transform="`translate(${it.x},0)`"
          >
            <rect width="10" height="10" rx="2" ry="2" :fill="it.color" />
            <text x="14" y="9" fill="#374151" font-size="12">{{ it.label }}</text>
          </g>
        </g>

        <g :transform="`translate(${margin.left},${margin.top + legendBlockH})`" shape-rendering="crispEdges">
          <g v-for="t in yTicks" :key="`t-${t}`">
            <line
              :x1="0" :y1="valueToY(t)"
              :x2="chartInnerWidth" :y2="valueToY(t)"
              stroke="#e5e7eb" stroke-width="1"
            />
            <text
              :x="-6" :y="valueToY(t)"
              text-anchor="end" dominant-baseline="middle"
              fill="#6b7280" font-size="11"
            >
              {{ formatTick(t) }}
            </text>
          </g>

          <g v-for="(g, gi) in groups" :key="g.channelId" :transform="`translate(${gi * (groupWidth + groupGap)},0)`">
            <g v-for="(h, hi) in headers" :key="`b-${g.channelId}-${h.key}`">
              <rect
                :x="barGroupOffset + hi * (barWidth + innerPad)"
                :y="valueToY(g.values[h.key])"
                :width="barWidth"
                :height="valueToH(g.values[h.key])"
                :fill="colorFor(h.key)"
                rx="2" ry="2"
              >
                <title>{{ g.channelLabel }} • {{ h.label }}: {{ formatValue(g.values[h.key], decimalsFor(g.channelId)) }}</title>
              </rect>
            </g>

            <text
              :x="groupWidth/2" :y="chartInnerHeight + 30"
              text-anchor="middle" fill="#6b7280" font-size="11"
            >
              <tspan :font-size="18" :fill="g.color">●</tspan>
              {{ shortLabel(g.channelLabel) }}
            </text>
          </g>
        </g>
      </svg>
    </div>
  </div>
  <div v-else class="hint">No data</div>
</template>

<script>
export default {
  name: "BarChart",
  emits: ["img-export"],
  props: {
    sltd_zone: { type: Object, required: true },
    headers:   { type: Array,  required: true },
    groups:    { type: Array,  required: true },
    height:    { type: Number, default: 280 },
    margin:    { type: Object, default: () => ({ top: 10, right: 16, bottom: 36, left: 44 }) },
    barWidth:  { type: Number, default: 14 },
    innerPad:  { type: Number, default: 6 },
    groupGap:  { type: Number, default: 18 },
    minGroupWidth: { type: Number, default: 90 },
    colorsByKey: {
      type: Object,
      default: () => ({
        instant_off_value:  "#2563eb",
        "4_hour_value":     "#16a34a",
        "10_hour_value":    "#f59e0b",
        "24_hour_value":    "#ef4444",
        max_depolarization: "#8b5cf6",
        last_depolarization:"#0891b2",
        voltage_applied:    "#84cc16",
        voltage_remaining:  "#f97316",
      }),
    },
    palette: {
      type: Array,
      default: () => ["#2563eb","#16a34a","#f59e0b","#ef4444","#8b5cf6","#0891b2","#84cc16","#f97316"],
    },
    automaticReport: { type: Boolean, default: false }
  },

  mounted() {
    if (this.automaticReport) this.$nextTick(this.exportImg);
  },
  watch: {
    headers: { deep: true, handler() { if (this.automaticReport) this.$nextTick(this.exportImg); } },
    groups:  { deep: true, handler() { if (this.automaticReport) this.$nextTick(this.exportImg); } },
    height() { if (this.automaticReport) this.$nextTick(this.exportImg); },
  },

  methods: {
    // Returns base64 PNG data URL
    async exportImg() {
      const svg = this.$refs.svgEl;
      if (!svg) return null;

      const clone = svg.cloneNode(true);
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const xml = new XMLSerializer().serializeToString(clone);

      const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const img  = new Image();

      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });

      const w = Number(svg.getAttribute("width"))  || svg.clientWidth  || 800;
      const h = Number(svg.getAttribute("height")) || svg.clientHeight || 400;

      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);

      const pngDataUrl = canvas.toDataURL("image/png");
      // Emit the chart's natural dimensions so the report can preserve aspect
      // ratio (the SVG width depends on group count, so it varies per chart).
      this.$emit("img-export", { url: pngDataUrl, width: w, height: h });
      return pngDataUrl;
    },

    colorFor(key) {
      if (this.colorsByKey && this.colorsByKey[key]) return this.colorsByKey[key];
      const i = Math.abs(this.hashStr(String(key))) % this.palette.length;
      return this.palette[i];
    },
    hashStr(s) {
      let h = 2166136261;
      for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h += (h<<1)+(h<<4)+(h<<7)+(h<<8)+(h<<24); }
      return h | 0;
    },
    valueToY(v) {
      if (!Number.isFinite(v) || v <= 0) return this.chartInnerHeight;
      return (1 - v / this.yMax) * this.chartInnerHeight;
    },
    valueToH(v) {
      if (!Number.isFinite(v) || v <= 0) return 0;
      return (v / this.yMax) * this.chartInnerHeight;
    },
    shortLabel(label) {
      const s = String(label ?? "");
      return s.length > 18 ? "…" + s.slice(-17) : s;
    },
    formatValue(v, decimals = 3) {
      if (v == null) return "-";
      if (Number.isFinite(Number(v))) {
        const n = Number(decimals);
        const d = Number.isInteger(n) && n >= 0 ? n : 3;
        return Number(v).toFixed(d);
      }
      return v;
    },
    formatTick(t) { return t.toFixed(0); },
    channelById(id) {
      const zone = this.sltd_zone;
      for (const node of zone.nodes) {
        for (const key in node.channels) {
          const ch = node.channels[key];
          if (ch.id === id) return ch;
        }
      }
      return null;
    },
    decimalsFor(id) {
      const ch = this.channelById(id);
      const dec = ch && ch.formula && Number.isFinite(Number(ch.formula.decimals))
        ? Number(ch.formula.decimals)
        : null;
      return dec != null ? dec : 3;
    },
  },

  computed: {
    legendTop() { return 8; },
    legendRowH() { return 18; },
    legendBlockH() { return this.headers.length ? this.legendRowH + 8 : 0; },
    chartInnerHeight() { return this.height - this.margin.top - this.margin.bottom - this.legendBlockH; },
    svgHeight() { return this.height + this.legendTop; },

    barsBlockWidth() {
      const n = this.headers.length;
      return n * this.barWidth + (n - 1) * this.innerPad;
    },
    barGroupOffset() { return Math.max(0, (this.groupWidth - this.barsBlockWidth) / 2); },
    groupWidth() { return Math.max(this.barsBlockWidth, this.minGroupWidth); },

    legendGap() { return 16; },
    legendNeededWidth() {
      if (!this.headers.length) return 0;
      const total = this.headers.reduce((sum, h) => {
        const label = String(h.label ?? "");
        const textW = 7 * label.length;
        return sum + (10 + 4 + textW) + this.legendGap;
      }, 0);
      return Math.max(0, total - this.legendGap);
    },
    legendItems() {
      let x = 0;
      return this.headers.map(h => {
        const label = String(h.label ?? "");
        const textW = 7 * label.length;
        const itemW = 10 + 4 + textW + this.legendGap;
        const item = { x, label, color: this.colorFor(h.key) };
        x += itemW;
        return item;
      });
    },

    chartInnerWidth() {
      const n = this.groups.length;
      const chartW = n ? n * this.groupWidth + (n - 1) * this.groupGap : 0;
      return Math.max(chartW, this.legendNeededWidth);
    },
    chartWidth() { return this.chartInnerWidth + this.margin.left + this.margin.right; },

    maxValue() {
      let m = 0;
      for (const g of this.groups) for (const h of this.headers) {
        const v = g.values?.[h.key];
        if (Number.isFinite(v)) m = Math.max(m, v);
      }
      return m || 1;
    },
    yStep() { return this.maxValue <= 100 ? 10 : 20; },
    yMax() { return Math.max(this.yStep, Math.ceil(this.maxValue / this.yStep) * this.yStep); },
    yTicks() {
      const ticks = [];
      for (let v = 0; v <= this.yMax; v += this.yStep) ticks.push(v);
      return ticks;
    },
  },
};
</script>

<style scoped>
.bar-chart-wrap { display: flex; flex-direction: column; gap: 8px; }
.svg-scroller { overflow-x: auto; border: 1px solid #eee; border-radius: 6px; background: #fff; }
.hint { margin-top: 6px; color: #666; }
.colored-symbol {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}
</style>
